<template>
  <gas-page-shell :breadcrumb="['设备管理', '趋势图']">
    <div class="usage-row">
      <div v-for="u in usageCards" :key="u.label" class="usage-card" :style="{ borderColor: u.color }">
        <i :class="u.icon" :style="{ color: u.color }"></i>
        <div>
          <div class="u-label">{{ u.label }}</div>
          <div class="u-val" :style="{ color: u.color }">{{ u.value }} <small>Nm³</small></div>
        </div>
      </div>
    </div>

    <div class="search-section">
      <div class="section-title"><span class="title-bar"></span>查询条件</div>
      <el-form :inline="true" size="small" label-width="88px">
        <el-form-item label="设备类型">
          <el-select v-model="terminalType" placeholder="请选择设备类型" style="width:160px" @change="onTerminalTypeChange">
            <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="协议">
          <el-select
            v-model="protocolId"
            filterable
            clearable
            placeholder="请选择协议"
            style="width:200px"
            @change="onProtocolChange"
          >
            <el-option
              v-for="p in customProtocolOptions"
              :key="p.protocolId"
              :label="`${p.protocolName}${p.protocolCode ? '（' + p.protocolCode + '）' : ''}`"
              :value="p.protocolId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="站点名称">
          <el-select
            v-model="siteName"
            filterable
            clearable
            placeholder="全部站点"
            style="width:180px"
            @change="onSiteChange"
          >
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备">
          <el-select
            v-model="deviceId"
            filterable
            remote
            clearable
            reserve-keyword
            :remote-method="remoteSearchDevices"
            :loading="deviceLoading"
            :disabled="!protocolId"
            :placeholder="!protocolId ? '请先选择协议' : '请输入设备名称、编号或站点'"
            style="width:260px"
            @change="onDeviceChange"
          >
            <el-option
              v-for="t in deviceOptions"
              :key="t.deviceId"
              :label="deviceOptionLabel(t)"
              :value="t.deviceId"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="showSubDeviceSelect" label="子设备">
          <el-select v-model="subDeviceCode" style="width:140px" @change="loadTrend">
            <el-option
              v-for="s in subDeviceOptions"
              :key="s.code"
              :label="s.label"
              :value="s.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="时间范围">
          <el-radio-group v-model="rangeHours" size="small" @change="onRangePresetChange">
            <el-radio-button :label="24">24小时</el-radio-button>
            <el-radio-button :label="168">7天</el-radio-button>
            <el-radio-button :label="720">30天</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="起始时间">
          <el-date-picker
            v-model="beginTime"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            placeholder="选择起始时间"
            style="width:190px"
            clearable
            @change="onCustomTimeChange"
          />
        </el-form-item>
        <el-form-item label="截止时间">
          <el-date-picker
            v-model="endTime"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            placeholder="选择截止时间"
            style="width:190px"
            clearable
            @change="onCustomTimeChange"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-refresh" @click="loadTrend">刷新</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="metric-tabs">
      <span
        v-for="m in metricOptions"
        :key="m.key"
        class="metric-tab"
        :class="{ active: metric === m.key }"
        @click="switchMetric(m.key)"
      >{{ m.label }}</span>
    </div>

    <div v-loading="chartLoading" class="chart-panel">
      <div class="chart-head">
        <div>
          <div class="chart-title">{{ chartTitle }}</div>
          <div class="chart-sub">{{ terminalLabel }} · {{ rangeLabel }}</div>
        </div>
        <div v-if="latestValue != null" class="latest">
          当前值 <strong>{{ latestValue }}</strong> {{ unit }}
        </div>
      </div>
      <div class="chart-stage">
        <div ref="chartEl" class="chart-box"></div>
        <div v-if="!chartLoading && !pointCount" class="empty-chart">暂无真实趋势数据</div>
      </div>
    </div>
  </gas-page-shell>
</template>

<script>
import * as echarts from 'echarts'
import GasPageShell from '@/components/GasPageShell.vue'
import { gasApi } from '@/api/gas'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import siteOptionsMixin from '@/mixins/siteOptions'
import { DEFAULT_MONITOR_DEVICE_TYPE, normalizeDeviceType } from '@/config/deviceTypeMetrics'
import { protocolParamsToMetrics, protocolsForDeviceType } from '@/config/protocolParamNames'

export default {
  components: { GasPageShell },
  mixins: [deviceTypeOptionsMixin, siteOptionsMixin],
  data() {
    return {
      usageCards: [],
      deviceOptions: [],
      deviceLoading: false,
      deviceSearchTimer: null,
      terminalType: DEFAULT_MONITOR_DEVICE_TYPE,
      protocolId: null,
      protocolName: '',
      customProtocolOptions: [],
      customProtocolMetrics: [],
      deviceId: null,
      deviceCode: '',
      siteName: '',
      terminalCode: '',
      terminalName: '',
      deviceType: DEFAULT_MONITOR_DEVICE_TYPE,
      subDeviceOptions: [],
      subDeviceCode: '',
      metric: 'stdInstant',
      rangeHours: 24,
      beginTime: '',
      endTime: '',
      chartLoading: false,
      trendData: null,
      chart: null
    }
  },
  computed: {
    metricOptions() {
      return this.customProtocolMetrics
    },
    showSubDeviceSelect() {
      return this.subDeviceOptions.length > 1
    },
    currentMetric() {
      return this.metricOptions.find(m => m.key === this.metric)
        || { key: this.metric, label: this.metric, unit: '' }
    },
    unit() {
      return (this.trendData && this.trendData.unit) || this.currentMetric.unit
    },
    chartTitle() {
      const label = (this.trendData && this.trendData.metricLabel) || this.currentMetric.label
      return `${label}趋势`
    },
    terminalLabel() {
      let base = this.deviceCode || this.terminalCode || '请选择设备'
      if (this.terminalName && this.terminalCode) {
        base = `${this.terminalName}（${this.terminalCode}）`
      } else if (this.terminalName && this.deviceCode) {
        base = `${this.terminalName}（${this.deviceCode}）`
      }
      const sub = this.subDeviceOptions.find(s => s.code === this.subDeviceCode)
      if (this.showSubDeviceSelect && sub && sub.label) {
        return `${base} · ${sub.label}`
      }
      return base
    },
    useCustomRange() {
      return !!(this.beginTime && this.endTime)
    },
    rangeLabel() {
      if (this.useCustomRange) {
        return `${this.beginTime} ~ ${this.endTime}`
      }
      if (this.rangeHours === 168) return '近7天'
      if (this.rangeHours === 720) return '近30天'
      return '近24小时'
    },
    latestValue() {
      const pts = (this.trendData && this.trendData.points) || []
      for (let i = pts.length - 1; i >= 0; i--) {
        if (pts[i] && pts[i].value != null) return pts[i].value
      }
      return null
    },
    pointCount() {
      return ((this.trendData && this.trendData.points) || []).length
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      handler(q) {
        this.applyRouteQuery(q || {})
        this.ensureDefaultTimeRange()
        this.bootstrapTrend()
      }
    }
  },
  created() {
    this.applyRouteQuery(this.$route.query || {})
    if (!this.terminalType) this.terminalType = DEFAULT_MONITOR_DEVICE_TYPE
    this.ensureDefaultTimeRange()
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadCustomProtocolOptions()
    this.loadUsage()
    this.bootstrapTrend()
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeChart)
    if (this.deviceSearchTimer) clearTimeout(this.deviceSearchTimer)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    applyRouteQuery(q) {
      if (q.terminalType) this.terminalType = q.terminalType
      if (q.protocolId) this.protocolId = Number(q.protocolId) || q.protocolId
      if (q.protocolName) this.protocolName = q.protocolName
      if (q.deviceId) this.deviceId = Number(q.deviceId) || null
      if (q.siteName) this.siteName = q.siteName
      if (q.deviceCode) this.deviceCode = q.deviceCode
      if (q.terminalCode) this.terminalCode = q.terminalCode
      if (!this.deviceCode && q.terminalCode) this.deviceCode = q.terminalCode
      if (q.deviceName) this.terminalName = q.deviceName
      if (q.terminalName) this.terminalName = q.terminalName
      if (q.metric) this.metric = q.metric
      if (q.subDeviceCode) this.subDeviceCode = q.subDeviceCode
      if (q.rangeHours) this.rangeHours = Number(q.rangeHours) || 24
      if (q.beginTime) this.beginTime = q.beginTime
      if (q.endTime) this.endTime = q.endTime
    },
    async bootstrapTrend() {
      await this.loadCustomProtocolOptions()
      if (this.protocolId || this.protocolName) {
        this.syncProtocolSelection()
        await this.loadCustomProtocolColumns()
      }
      await this.loadDevices(this.deviceCode || this.terminalCode || '', true)
    },
    syncProtocolSelection() {
      const list = this.customProtocolOptions || []
      const hit = list.find(p => Number(p.protocolId) === Number(this.protocolId))
        || list.find(p => p.protocolName === this.protocolName || p.protocolCode === this.protocolName)
      if (hit) {
        this.protocolId = hit.protocolId
        this.protocolName = hit.protocolName || ''
      }
    },
    async loadCustomProtocolOptions() {
      try {
        const res = await gasApi.protocol.list({ pageNum: 1, pageSize: 500 })
        this.customProtocolOptions = protocolsForDeviceType((res.data && res.data.records) || [], this.terminalType)
      } catch (e) {
        this.customProtocolOptions = []
      }
    },
    async loadCustomProtocolColumns() {
      if (!this.protocolId) {
        this.customProtocolMetrics = []
        this.ensureMetricForDeviceType()
        return
      }
      try {
        const res = await gasApi.protocol.paramList({
          protocolId: this.protocolId,
          pageNum: 1,
          pageSize: 200
        })
        const records = (res.data && res.data.records) || []
        const protocol = (this.customProtocolOptions || []).find(p => p.protocolId === this.protocolId)
        this.customProtocolMetrics = protocolParamsToMetrics(records, this.terminalType, protocol)
      } catch (e) {
        this.customProtocolMetrics = []
      }
      this.ensureMetricForDeviceType()
    },
    async onTerminalTypeChange() {
      this.deviceId = null
      this.deviceCode = ''
      this.terminalCode = ''
      this.terminalName = ''
      this.deviceType = this.terminalType
      this.subDeviceOptions = []
      this.subDeviceCode = ''
      this.trendData = null
      this.protocolId = null
      this.protocolName = ''
      this.customProtocolMetrics = []
      this.deviceOptions = []
      this.metric = ''
      await this.loadCustomProtocolOptions()
    },
    async onProtocolChange(protocolId) {
      const hit = (this.customProtocolOptions || []).find(p => p.protocolId === protocolId)
      this.protocolId = protocolId || null
      this.protocolName = hit ? (hit.protocolName || '') : ''
      this.deviceId = null
      this.deviceCode = ''
      this.terminalCode = ''
      this.terminalName = ''
      this.subDeviceOptions = []
      this.subDeviceCode = ''
      this.trendData = null
      await this.loadCustomProtocolColumns()
      if (!this.protocolId) {
        this.deviceOptions = []
        return
      }
      await this.loadDevices('', true)
    },
    async onSiteChange() {
      this.deviceId = null
      this.deviceCode = ''
      this.terminalCode = ''
      this.terminalName = ''
      this.subDeviceOptions = []
      this.subDeviceCode = ''
      this.trendData = null
      await this.loadDevices('', true)
    },
    ensureDefaultTimeRange() {
      if (this.beginTime && this.endTime) return
      this.setPresetTimeRange()
    },
    setPresetTimeRange() {
      const days = this.rangeHours === 720 ? 30 : this.rangeHours === 168 ? 7 : 1
      const start = new Date()
      start.setDate(start.getDate() - days)
      start.setHours(0, 0, 0, 0)
      const end = new Date()
      end.setHours(23, 59, 59, 0)
      this.beginTime = this.formatDateTime(start)
      this.endTime = this.formatDateTime(end)
    },
    formatDateTime(date) {
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    onRangePresetChange() {
      this.setPresetTimeRange()
      this.loadTrend()
    },
    onCustomTimeChange() {
      if (this.beginTime && this.endTime && this.beginTime > this.endTime) {
        this.$message.warning('起始时间不能晚于截止时间')
        return
      }
      // 只选其一：缺失的一端按当前档位补齐，保证横轴立即按所选时间刷新
      if (this.beginTime && !this.endTime) {
        this.endTime = this.formatDateTime(new Date())
      } else if (!this.beginTime && this.endTime) {
        const days = this.rangeHours === 720 ? 30 : this.rangeHours === 168 ? 7 : 1
        const start = new Date(this.endTime.replace(/-/g, '/'))
        start.setDate(start.getDate() - days)
        this.beginTime = this.formatDateTime(start)
      }
      this.loadTrend()
    },
    async loadUsage() {
      const res = await gasApi.terminal.monitorUsage()
      const d = res.data || {}
      this.usageCards = [
        { label: '上月用量', value: (d.lastMonthUsage || 0).toLocaleString(), icon: 'el-icon-date', color: '#1677ff' },
        { label: '当月用量', value: (d.currentMonthUsage || 0).toLocaleString(), icon: 'el-icon-data-line', color: '#00b42a' },
        { label: '累计用量', value: (d.totalUsage || 0).toLocaleString(), icon: 'el-icon-pie-chart', color: '#ff7d00' }
      ]
    },
    remoteSearchDevices(keyword) {
      if (this.deviceSearchTimer) clearTimeout(this.deviceSearchTimer)
      this.deviceSearchTimer = setTimeout(() => {
        this.loadDevices(keyword || '', false)
      }, 250)
    },
    async loadDevices(keyword = '', ensureSelection = false) {
      if (!this.protocolId) {
        this.deviceOptions = []
        if (ensureSelection) {
          this.applyDevice(null)
          this.trendData = null
        }
        return
      }
      this.deviceLoading = true
      try {
        const params = { pageNum: 1, pageSize: 50 }
        if (keyword) params.keyword = keyword
        if (this.terminalType) params.terminalType = this.terminalType
        if (this.protocolName) params.protocolName = this.protocolName
        if (this.siteName) params.siteName = this.siteName
        const res = await gasApi.device.list(params)
        const records = (res.data && res.data.records) || []
        this.deviceOptions = records
        if (ensureSelection) {
          const current = this.deviceId
            ? records.find(d => Number(d.deviceId) === Number(this.deviceId))
            : null
          if (current) {
            this.applyDevice(current)
          } else if ((this.deviceCode || this.terminalCode) && records.length) {
            this.applyDevice(records[0])
          } else if (!this.deviceId && records.length) {
            this.applyDevice(records[0])
          } else if (!records.length) {
            this.applyDevice(null)
          }
          await this.loadSubDevices()
          await this.loadTrend()
        }
      } finally {
        this.deviceLoading = false
      }
    },
    deviceOptionLabel(row) {
      const name = row.deviceName || row.deviceCode || '未命名设备'
      const code = row.deviceCode || row.hardwareCode || ''
      const site = row.siteName ? ` · ${row.siteName}` : ''
      return code ? `${name} (${code})${site}` : `${name}${site}`
    },
    applyDevice(row) {
      if (!row) {
        this.deviceId = null
        this.deviceCode = ''
        this.terminalCode = ''
        this.terminalName = ''
        this.deviceType = ''
        this.subDeviceOptions = []
        this.subDeviceCode = ''
        this.ensureMetricForDeviceType()
        return
      }
      this.deviceId = row.deviceId || null
      this.deviceCode = row.deviceCode || ''
      this.terminalCode = row.deviceCode || row.hardwareCode || ''
      this.terminalName = row.deviceName || row.deviceCode || ''
      this.deviceType = row.terminalType || row.deviceType || this.terminalType
      this.ensureMetricForDeviceType()
    },
    async loadSubDevices() {
      if (!this.deviceId || normalizeDeviceType(this.terminalType) === '压力远传') {
        this.subDeviceOptions = []
        this.subDeviceCode = ''
        return
      }
      const preferred = this.subDeviceCode
      try {
        const res = await gasApi.device.subDevices(this.deviceId)
        const list = (res.data || []).map((s, idx) => ({
          code: (s && s.code) || ('m' + (idx + 1)),
          label: (s && s.label) || ('设备' + (idx + 1))
        }))
        this.subDeviceOptions = list
        if (list.length > 1) {
          const stillValid = list.some(s => s.code === preferred)
          const first = list.find(s => s.code === 'm1') || list[0]
          this.subDeviceCode = stillValid ? preferred : ((first && first.code) || 'm1')
        } else {
          this.subDeviceCode = ''
        }
      } catch (e) {
        this.subDeviceOptions = []
        this.subDeviceCode = ''
      }
    },
    async onDeviceChange(deviceId) {
      const row = this.deviceOptions.find(d => Number(d.deviceId) === Number(deviceId))
      this.applyDevice(row)
      this.subDeviceCode = ''
      await this.loadSubDevices()
      this.loadTrend()
    },
    ensureMetricForDeviceType() {
      const options = this.metricOptions || []
      if (!options.length) {
        this.metric = ''
        return
      }
      if (!options.some(m => m.key === this.metric)) {
        this.metric = options[0].key
      }
    },
    switchMetric(key) {
      this.metric = key
      this.loadTrend()
    },
    async loadTrend() {
      if (!this.protocolId) {
        this.trendData = null
        this.$nextTick(() => this.chart && this.chart.clear())
        return
      }
      if (!this.deviceId && !this.deviceCode && !this.terminalCode) {
        this.trendData = null
        this.$nextTick(() => this.chart && this.chart.clear())
        return
      }
      if (!this.metric) {
        this.trendData = null
        this.$nextTick(() => this.chart && this.chart.clear())
        return
      }
      if (this.beginTime && this.endTime && this.beginTime > this.endTime) {
        this.$message.warning('起始时间不能晚于截止时间')
        return
      }
      this.chartLoading = true
      try {
        const params = {
          metric: this.metric,
          rangeHours: this.rangeHours
        }
        if (this.deviceId) params.deviceId = this.deviceId
        if (this.deviceCode) params.deviceCode = this.deviceCode
        if (this.terminalCode) params.terminalCode = this.terminalCode
        if (this.beginTime) params.beginTime = this.beginTime
        if (this.endTime) params.endTime = this.endTime
        if (this.showSubDeviceSelect && this.subDeviceCode) {
          params.subDeviceCode = this.subDeviceCode
        }
        const res = await gasApi.terminal.monitorTrend(params)
        this.trendData = res.data || {}
        this.$nextTick(() => this.renderChart())
      } finally {
        this.chartLoading = false
      }
    },
    parseTrendTime(raw) {
      if (raw == null || raw === '') return NaN
      if (typeof raw === 'number') return raw
      return new Date(String(raw).trim().replace('T', ' ').replace(/-/g, '/')).getTime()
    },
    formatTrendAxisLabel(ms, spanMs) {
      const d = new Date(ms)
      const pad = n => String(n).padStart(2, '0')
      if (spanMs <= 24 * 3600 * 1000) return `${pad(d.getHours())}:${pad(d.getMinutes())}`
      if (spanMs <= 30 * 24 * 3600 * 1000) return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    renderChart() {
      const el = this.$refs.chartEl
      if (!el) return
      if (!this.chart) this.chart = echarts.init(el)
      const pts = (this.trendData && this.trendData.points) || []
      if (!pts.length) {
        this.chart.clear()
        return
      }
      const beginMs = this.parseTrendTime(this.trendData && this.trendData.beginTime)
      const endMs = this.parseTrendTime(this.trendData && this.trendData.endTime)
      const useTimeAxis = Number.isFinite(beginMs) && Number.isFinite(endMs) && endMs > beginMs
      const labels = pts.map(p => p.label)
      const values = pts.map(p => (p.value == null ? null : Number(p.value)))
      let seriesData = values
      if (useTimeAxis) {
        seriesData = pts
          .map(p => [this.parseTrendTime(p.time || p.bucketTime), p.value == null ? null : Number(p.value)])
          .filter(p => Number.isFinite(p[0]))
      }
      const spanMs = useTimeAxis ? endMs - beginMs : 0
      const timeAxis = {
        type: 'time',
        min: beginMs,
        max: endMs,
        axisLine: { lineStyle: { color: '#e5e6eb' } },
        axisLabel: {
          color: '#86909c',
          fontSize: 11,
          formatter: value => this.formatTrendAxisLabel(value, spanMs)
        }
      }
      const categoryAxis = {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: '#e5e6eb' } },
        axisLabel: { color: '#86909c', fontSize: 11 }
      }
      this.chart.setOption({
        grid: { left: 48, right: 24, top: 32, bottom: 40 },
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            const item = Array.isArray(params) ? params[0] : params
            const val = item && item.value
            const shown = Array.isArray(val) ? val[1] : val
            return `${item.axisValue}<br/>${item.marker}${shown == null ? '无上报数据' : shown}`
          }
        },
        xAxis: useTimeAxis ? timeAxis : categoryAxis,
        yAxis: {
          type: 'value',
          name: this.unit,
          axisLine: { show: false },
          splitLine: { lineStyle: { color: '#f2f3f5' } },
          axisLabel: { color: '#86909c' }
        },
        series: [{
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          data: seriesData,
          lineStyle: { width: 2, color: '#1677ff' },
          itemStyle: { color: '#1677ff' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(22,119,255,0.25)' },
              { offset: 1, color: 'rgba(22,119,255,0.02)' }
            ])
          }
        }]
      }, true)
      this.resizeChart()
    },
    resizeChart() {
      if (this.chart) this.chart.resize()
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';

.usage-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}
.usage-card {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-left-width: 4px;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  i { font-size: 28px; }
}
.u-label { font-size: 13px; color: #86909c; }
.u-val { font-size: 22px; font-weight: 600; small { font-size: 12px; font-weight: 400; } }

.search-section {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
}
.section-title { display: flex; align-items: center; font-weight: 600; margin-bottom: 12px; }
.title-bar { width: 3px; height: 14px; background: #1677ff; margin-right: 8px; }

.metric-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.metric-tab {
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid #e5e6eb;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  color: #4e5969;
  transition: all 0.15s;
}
.metric-tab:hover { border-color: #1677ff; color: #1677ff; }
.metric-tab.active {
  background: #e8f3ff;
  border-color: #1677ff;
  color: #1677ff;
  font-weight: 600;
}

.chart-panel {
  background: #fff;
  border: 1px solid #e5e6eb;
  border-radius: 8px;
  padding: 16px 20px 12px;
}
.chart-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 12px;
}
.chart-title { font-size: 16px; font-weight: 600; color: #1d2129; }
.chart-sub { font-size: 12px; color: #86909c; margin-top: 4px; }
.latest { font-size: 13px; color: #86909c; }
.latest strong { font-size: 20px; color: #1677ff; margin: 0 4px; }
.chart-stage { position: relative; }
.chart-box { width: 100%; height: 360px; }
.empty-chart {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #86909c;
  font-size: 13px;
  background: #fafbfc;
}

@media (max-width: 960px) {
  .usage-row { grid-template-columns: 1fr; }
}
</style>
